// Fixed-order game-tick pipeline (ENG-20). Runs at 20 TPS via the client loop /
// server. The 11 phases run in a documented, deterministic order every tick so
// solo, server, and CI stay bit-identical (NET prerequisite). Most phases are
// scaffolds now — scheduled ticks (phase 2) and random ticks (phase 3) are live;
// fluids/redstone/entities/etc. fill in at their milestones via hooks.
import { TICK, WORLD } from '../config';
import { localIndex } from './section';
import type { Notification } from './notify';
import type { World } from './world';

/** The canonical tick order (ARCH §5.5). Do not reorder. */
export const TICK_PHASES = [
  'time',
  'scheduledTicks',
  'randomTicks',
  'fluids',
  'redstone',
  'blockEntities',
  'entities',
  'player',
  'bosses',
  'spawns',
  'advancements',
] as const;
export type TickPhase = (typeof TICK_PHASES)[number];

/** Callbacks the later milestones supply (block behaviors, AI, …). All optional;
 *  tests inject spies. `phase` is invoked at the start of every phase (ordering). */
export interface TickHooks {
  phase?(name: TickPhase): void;
  onScheduledTick?(x: number, y: number, z: number): void;
  /** The §27.9 neighbour bus, drained in the SAME `scheduledTicks` phase but through
   *  its own hook: notifications are not scheduled ticks, and feeding them to
   *  `onScheduledTick` would hand redstone and fluids 6 phantom updates per edit
   *  (D-TF-205). Called once per tick with the whole batch, after the scheduled ticks
   *  themselves, so a handler sees the world those ticks already settled. */
  onNotify?(batch: readonly Notification[]): void;
  onRandomTick?(x: number, y: number, z: number, state: number): void;
  /** Flowing fluids (§5.5 phase 4) — drains the world's fluid queue (M9.1). */
  onFluids?(): void;
  /** Block entities (§5.5 phase 6) — furnaces cook, spawners fire (M6). */
  onBlockEntities?(): void;
  /** Script dispatch, inside the SAME phase 6 (ARCH §27.4 tick placement, binding):
   *  block `on redstone` plus, from M46, the block cascade drain. Kept separate from
   *  `onBlockEntities` so the ScriptSystem's placement belongs to this pipeline rather
   *  than to whichever host happens to call it. */
  onScripts?(): void;
  /** Entity AI + physics (§5.5 phase 7) — the EntitySystem (M5). */
  onEntities?(): void;
  /** Despawn/spawn attempts (§5.5 phase 10) — the mob spawner (M5). */
  onSpawns?(): void;
}

function runRandomTicks(world: World, hooks: TickHooks): void {
  if (!hooks.onRandomTick) return; // no consumers yet → skip the work entirely
  const speed = TICK.RANDOM_TICK_SPEED;
  for (const chunk of world.loadedChunks()) {
    const bx = chunk.cx * WORLD.SECTION;
    const bz = chunk.cz * WORLD.SECTION;
    for (let si = 0; si < WORLD.SECTIONS_PER_COLUMN; si++) {
      if (!chunk.sections[si]) continue;
      const baseY = (si - WORLD.SECTION_Y_OFFSET) * WORLD.SECTION;
      for (let k = 0; k < speed; k++) {
        const li = world.tickRng.nextInt(WORLD.SECTION * WORLD.SECTION * WORLD.SECTION);
        const lx = li & 15;
        const lz = (li >> 4) & 15;
        const ly = (li >> 8) & 15;
        const state = chunk.getBlockState(lx, baseY + ly, lz);
        hooks.onRandomTick(bx + lx, baseY + ly, bz + lz, state);
      }
    }
  }
}

/** Advance the world exactly one game tick. */
export function runTick(world: World, hooks: TickHooks = {}): void {
  for (const phase of TICK_PHASES) {
    hooks.phase?.(phase);
    switch (phase) {
      case 'time':
        // Absolute world time advances unless the daylight cycle is off (LIT-6);
        // time-of-day is dayTime % DAY_TICKS, so it never needs wrapping here.
        if (world.gamerules.get('doDaylightCycle') !== 'false') world.dayTime++;
        world.advanceWeather(); // PLR-10 (§5.5 phase 1)
        break;
      case 'scheduledTicks': {
        if (hooks.onScheduledTick) {
          for (const t of world.takeDueTicks()) hooks.onScheduledTick(t.x, t.y, t.z);
        } else {
          world.takeDueTicks(); // drain so the queue can't grow unbounded
        }
        // The §27.9 bus drains here too, and ALWAYS — with no consumer the batch is
        // discarded rather than left to accumulate, exactly as the scheduled-tick
        // queue above does. Draining unconditionally also keeps the queue's observable
        // size a function of the world's edits and not of who happens to be listening.
        const batch = world.notifications.drain();
        if (batch.length > 0) hooks.onNotify?.(batch);
        break;
      }
      case 'randomTicks':
        runRandomTicks(world, hooks);
        break;
      case 'fluids':
        hooks.onFluids?.();
        break;
      case 'blockEntities':
        hooks.onBlockEntities?.();
        // Script blocks dispatch inside phase 6, after the block entities themselves
        // (§27.4: `blockEntities` → block `on redstone` + the block cascade drain).
        hooks.onScripts?.();
        break;
      case 'entities':
        hooks.onEntities?.();
        break;
      case 'spawns':
        hooks.onSpawns?.();
        break;
      // time / fluids / redstone / blockEntities / player / bosses /
      // advancements — scaffolds until their milestones (§18 map).
      default:
        break;
    }
  }
  world.tickCount++;
}

// Re-exported so callers can index sections consistently in tests.
export { localIndex };
